import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Replace } from 'lucide-react';
import type { UnitStatus } from '@platform/shared-types';
import {
  hardwareApi, EXCEPTION_FLOW, REASON_REQUIRED, UNIT_STATUS_LABEL, type DeviceUnitRow, type PendingReplacementRow,
} from '../../services/hardwareApi';
import { toastService } from '../../services/toastService';
import { unitLabel } from './stageShell';
import { useReplaceWizard } from './replaceWizardHost';
import { PRE_DISPATCH, canReplaceShipped } from './replaceFlow';

/** Mirrors the wizard's rules — for OFFERING it; the server still decides. */
const replaceRoad = (u: DeviceUnitRow | undefined): 'pre' | 'post' | null =>
  !u?.orderId ? null : PRE_DISPATCH.has(u.state) ? 'pre' : canReplaceShipped(u) ? 'post' : null;

/** Where a unit taken off its order for a replacement can still be found. */
const OFF_ORDER_REPLACE_STATES = new Set(['on_hold', 'in_stock', 'rma', 'retired', 'lost']);

/**
 * SOMETHING IS WRONG WITH THIS DEVICE — say so from where you noticed.
 *
 * Reporting a problem already existed, at the very bottom of the order screen:
 * scroll past every stage to the device tracker, find the row, change a state,
 * type a reason. That is a long way from the bench where a screen was found
 * cracked or a reader refused a key load — so in practice the person holding
 * the device told somebody instead, and the order said nothing.
 *
 * This is the same action, offered beside the device wherever it appears. It
 * writes exactly what the tracker writes: `setUnitStatus` with a state from
 * `EXCEPTION_FLOW` and, where the contract demands one, a `holdReason` — the
 * WORD FOR WORD text the merchant is shown. There is no second store of
 * problems, no ticket type of its own, nothing that has to be reconciled
 * later; the device tracker at the foot of the page shows this the moment it
 * is saved, because it is reading the same field.
 *
 * `onUnitChanged` patches the one unit in place — reporting a fault must not
 * reload the rail and throw away the place somebody was working.
 */

const PROBLEM_HINT: Partial<Record<UnitStatus, string>> = {
  on_hold: 'Paused — the merchant sees the reason',
  backordered: 'Waiting on stock — the merchant sees the reason',
  returned: 'Came back from the merchant',
  rma: 'Sent back to the vendor',
  retired: 'Out of service for good',
  lost: 'Never arrived — a carrier claim, not a return',
};

const ReportProblemButton: React.FC<{
  /** The devices this button speaks for. More than one asks which first. */
  units: DeviceUnitRow[];
  onUnitChanged?: (unit: DeviceUnitRow) => void;
  /** A quieter button where the row is already busy. */
  subtle?: boolean;
}> = ({ units, onUnitChanged, subtle }) => {
  const [open, setOpen] = useState(false);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [state, setState] = useState<UnitStatus | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  /** A replacement of the chosen unit that was started and not finished. */
  const [pending, setPending] = useState<{ orderId: string; row: PendingReplacementRow } | null>(null);
  /* The wizard is handed to the page's ReplaceWizardHost: this button sits in
     a row keyed on the device being replaced, and that row is gone the moment
     the swap takes the device off the order. */
  const wizard = useReplaceWizard();
  const reread = (id: string) => {
    hardwareApi.getUnit(id).then((r) => r?.unit && onUnitChanged?.(r.unit)).catch(() => {});
  };

  const live = units.filter(Boolean);
  const chosen = live.find((u) => u.unitId === unitId) || (live.length === 1 ? live[0] : undefined);

  /*
    IS A REPLACEMENT OF THIS DEVICE ALREADY UNDER WAY? Read only while the
    dialog is open. A bench swap takes the device off its order, so its order
    is found from the "replace_started" line the wizard writes in its history.
  */
  useEffect(() => {
    setPending(null);
    if (!open || !chosen) return undefined;
    let alive = true;
    (async () => {
      let orderId = chosen.orderId;
      if (!orderId && OFF_ORDER_REPLACE_STATES.has(chosen.state)) {
        const tl = await hardwareApi.getUnitTimeline(chosen.unitId, { limit: 25 }).catch(() => null);
        orderId = tl?.items?.find((e) => e.kind === 'replace_started')?.meta?.orderId;
      }
      if (!orderId) return;
      const o = (await hardwareApi.getOrder(orderId).catch(() => null))?.order;
      const row = o?.pendingReplacements?.[chosen.unitId];
      if (alive && row) setPending({ orderId, row });
    })();
    return () => { alive = false; };
  }, [open, chosen?.unitId, chosen?.orderId, chosen?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (live.length === 0) return null;
  const road = pending ? null : replaceRoad(chosen);
  const needsReason = state ? REASON_REQUIRED.has(state) : false;
  const ready = !!chosen && !!state && (!needsReason || reason.trim().length > 0);

  const close = () => {
    setOpen(false);
    setUnitId(null);
    setState(null);
    setReason('');
  };

  const save = async () => {
    if (!chosen || !state || !ready) return;
    setBusy(true);
    try {
      const res = await hardwareApi.setUnitStatus(chosen.unitId, {
        state,
        ...(needsReason ? { holdReason: reason.trim() } : {}),
      } as any);
      if (res?.unit) onUnitChanged?.(res.unit);
      toastService.success(
        `${unitLabel(chosen)} — ${UNIT_STATUS_LABEL[state] || state}.`
        + (needsReason ? ' The merchant sees the reason you gave.' : ''),
      );
      close();
    } catch (e: any) {
      /* The service's own sentence: it names the transition it refused. */
      toastService.error(e?.serverMessage || e?.message || 'Could not record that problem.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Report a problem with this device"
        aria-label={`Report a problem with ${live.length === 1 ? unitLabel(live[0]!) : 'a device on this pair'}`}
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition-colors ${
          subtle
            ? 'border-transparent text-stone-300 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700'
            : 'border-stone-200 bg-white text-stone-400 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700'
        }`}
      >
        <AlertTriangle size={14} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={close}
        >
          <div
            className="w-full max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">Report a problem</h3>
              <p className="mt-1 text-[12.5px] text-stone-500">
                This is the same record the device tracker at the foot of this order writes — it shows
                up there, and on the merchant&apos;s own progress, the moment you save.
              </p>
            </div>

            <div className="max-h-[60vh] space-y-4 overflow-y-auto px-5 py-4">
              {live.length > 1 && (
                <div>
                  <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-wide text-stone-400">
                    Which device
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {live.map((u) => (
                      <button
                        key={u.unitId}
                        type="button"
                        onClick={() => setUnitId(u.unitId)}
                        className={`min-h-[40px] rounded-lg border px-3 font-mono text-[12px] font-semibold ${
                          chosen?.unitId === u.unitId
                            ? 'border-[#DC143C] bg-[#FFF1F3] text-[#9F1239]'
                            : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50'
                        }`}
                      >
                        {unitLabel(u)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* THE GUIDED SWAP — first, because "this device has to go" is
                  what most reports here turn into, and the status-only
                  options below undo none of what the device is tied to. */}
              {chosen && pending && (
                <button
                  type="button"
                  onClick={() => { const u = chosen; const p = pending; close(); wizard.open({ unit: u, resume: p.row, orderId: p.orderId, onChanged: () => reread(u.unitId) }); }}
                  className="flex w-full min-h-[56px] items-center gap-3 rounded-xl border border-[#DC143C] bg-[#FFF1F3] px-3 py-2.5 text-left hover:bg-[#FFE4E8]"
                >
                  <Replace size={18} className="shrink-0 text-[#DC143C]" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-bold text-[#9F1239]">Replacement not finished — Continue</span>
                    <span className="block text-[11.5px] text-[#9F1239]/80">
                      A replacement of {unitLabel(chosen)} was started and stopped part-way. Continue picks up where it stopped.
                    </span>
                  </span>
                  <ArrowRight size={16} className="shrink-0 text-[#DC143C]" />
                </button>
              )}
              {chosen && road && (
                <button
                  type="button"
                  onClick={() => { const u = chosen; close(); wizard.open({ unit: u, onChanged: () => reread(u.unitId) }); }}
                  className="flex w-full min-h-[56px] items-center gap-3 rounded-xl border border-[#DC143C] bg-[#FFF1F3] px-3 py-2.5 text-left hover:bg-[#FFE4E8]"
                >
                  <Replace size={18} className="shrink-0 text-[#DC143C]" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-bold text-[#9F1239]">Replace this device</span>
                    <span className="block text-[11.5px] text-[#9F1239]/80">
                      {road === 'pre'
                        ? <>Guided: takes it off the order, undoes its register, reader, VAR sheet and pairing,
                          sends it to stock or Damage, and hands everything to the one you pick.</>
                        : <>It has shipped — a field replacement: records why (returned, lost in transit, warranty,
                          damaged), sends one from stock, and hands its register, reader, VAR sheet and pairing over.
                          Billing is left for a person to review; nothing is charged.</>}
                    </span>
                  </span>
                  <ArrowRight size={16} className="shrink-0 text-[#DC143C]" />
                </button>
              )}

              <div>
                <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-wide text-stone-400">
                  {chosen && (road || pending) ? 'Or just record what happened' : 'What happened'}
                </p>
                {/* Custom control, never a native select — platform rule, and
                    each option carries the hint that says what it MEANS. */}
                <div className="space-y-1.5">
                  {(EXCEPTION_FLOW as readonly UnitStatus[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setState(s)}
                      className={`flex w-full min-h-[44px] items-start gap-3 rounded-lg border px-3 py-2 text-left ${
                        state === s
                          ? 'border-[#DC143C] bg-[#FFF1F3]'
                          : 'border-stone-200 bg-white hover:bg-stone-50'
                      }`}
                    >
                      <span className={`mt-[3px] grid h-4 w-4 shrink-0 place-items-center rounded-full border ${
                        state === s ? 'border-[#DC143C] bg-[#DC143C]' : 'border-stone-300 bg-white'}`}
                      >
                        {state === s && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12.5px] font-semibold text-stone-800">
                          {UNIT_STATUS_LABEL[s] || s}
                        </span>
                        {PROBLEM_HINT[s] && (
                          <span className="block text-[11px] text-stone-500">{PROBLEM_HINT[s]}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {needsReason && (
                <div>
                  <label
                    htmlFor="report-problem-reason"
                    className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-wide text-stone-400"
                  >
                    Reason
                  </label>
                  <input
                    id="report-problem-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="The merchant sees this word for word"
                    autoFocus
                    className="min-h-[44px] w-full rounded-lg border border-stone-300 px-3 text-[13px] text-stone-800 placeholder-stone-400 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/10"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={close}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!ready || busy}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#9F1239] disabled:opacity-40"
              >
                {busy && <RubyGemMark size={13} drawing color="currentColor" />}
                Report it
              </button>
            </div>
          </div>
        </div>
      )}

      {wizard.element}
    </>
  );
};

export default ReportProblemButton;
